import React, { useEffect, useState } from "react";
import { executeMutation, executeQuery } from "../../../graphql/client";

// #205 Admin SMS tab (Twilio). Weekly-schedule blast with an editable
// message, consent-gated recipient count, and per-send results. All
// recipients have emailSmsConsentAt and no smsOptOutAt — the server
// enforces this; this tab just shows the numbers.

const btn = (primary) => ({
  border: primary ? "none" : "1px solid #d7dbe4",
  background: primary ? "var(--ab-coral, #ff6b4a)" : "#f6f7fa",
  color: primary ? "#fff" : "#333",
  borderRadius: 8,
  padding: "8px 18px",
  fontWeight: 700,
  fontSize: 14,
  cursor: "pointer",
});

const SmsTab = () => {
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const out = await executeQuery(`query SmsPreview { smsPreview }`);
      const data = out?.smsPreview ? JSON.parse(out.smsPreview) : null;
      setPreview(data);
      setMessage((prev) => prev || data?.suggestedText || "");
    } catch (err) {
      setError(err.message || "Couldn't load SMS status.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async () => {
    if (!preview?.eligibleCount) return;
    if (
      !window.confirm(
        `Send this text to ${preview.eligibleCount} member${
          preview.eligibleCount === 1 ? "" : "s"
        }? This cannot be undone.`
      )
    ) {
      return;
    }
    setSending(true);
    setError("");
    setResult(null);
    try {
      const out = await executeMutation(
        `mutation SendSmsBlast($message: String!) { sendSmsBlast(message: $message) }`,
        { message }
      );
      setResult(out?.sendSmsBlast ? JSON.parse(out.sendSmsBlast) : null);
    } catch (err) {
      setError(err.message || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ maxWidth: 700 }}>
      <h2 style={{ marginTop: 0 }}>📱 SMS Blast</h2>
      <p style={{ color: "#6a7385", fontSize: 14 }}>
        Sends only to members who agreed to texts at signup (or on the
        old join form) and haven't replied STOP. The box below pre-fills
        this week's schedule — edit freely before sending. Always keep
        the "Reply STOP" line.
      </p>

      {loading && <p style={{ color: "#6a7385" }}>Loading…</p>}
      {error && (
        <div
          style={{
            background: "#fdecea",
            border: "1px solid #e7b3ba",
            color: "#b00020",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 12,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {preview && !preview.configured && (
        <div
          style={{
            background: "#fdf7e3",
            border: "1px solid #f0d47a",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 12,
            fontSize: 14,
          }}
        >
          ⚠️ Twilio isn't configured yet. Add the GitHub repo secrets
          TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, and TWILIO_FROM_NUMBER,
          then redeploy the backend. Preview below still works.
        </div>
      )}

      {preview && (
        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 12,
            fontSize: 14,
          }}
        >
          <span>
            <strong>{preview.eligibleCount}</strong> eligible recipient
            {preview.eligibleCount === 1 ? "" : "s"}
          </span>
          {preview.fromNumber && (
            <span style={{ color: "#6a7385" }}>
              From: {preview.fromNumber}
            </span>
          )}
        </div>
      )}

      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={8}
        maxLength={1200}
        style={{
          width: "100%",
          fontFamily: "inherit",
          fontSize: 14,
          padding: 10,
          borderRadius: 8,
          border: "1px solid #d7dbe4",
          marginBottom: 4,
        }}
      />
      <div style={{ fontSize: 12, color: "#8a93a3", marginBottom: 10 }}>
        {message.length}/1200 · ~{Math.max(1, Math.ceil(message.length / 153))}{" "}
        SMS segment{message.length > 153 ? "s" : ""} per recipient
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <button type="button" style={btn(false)} onClick={load} disabled={loading}>
          Refresh
        </button>
        <button
          type="button"
          style={{
            ...btn(true),
            opacity:
              sending || !preview?.configured || !preview?.eligibleCount
                ? 0.5
                : 1,
          }}
          onClick={send}
          disabled={sending || !preview?.configured || !preview?.eligibleCount}
        >
          {sending
            ? "Sending…"
            : `Send to ${preview?.eligibleCount ?? 0} member${
                preview?.eligibleCount === 1 ? "" : "s"
              }`}
        </button>
      </div>

      {result && (
        <div
          style={{
            marginTop: 14,
            background: result.failedCount ? "#fdf7e3" : "#eef7ee",
            border: `1px solid ${result.failedCount ? "#f0d47a" : "#b9dcb5"}`,
            borderRadius: 8,
            padding: "10px 14px",
            fontSize: 14,
          }}
        >
          ✅ Sent {result.sentCount} · ❌ Failed {result.failedCount}
          {result.failedCount > 0 && (
            <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 13 }}>
              {result.results
                .filter((r) => !r.sent)
                .slice(0, 10)
                .map((r) => (
                  <li key={r.phone}>
                    {r.name || r.phone}: {r.error}
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default SmsTab;
